import {
  Info as InfoIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ReceiptLong as ReceiptIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  emitWarrantyInvoice,
  getSGRWarrantyRequestInvoicing,
} from 'lib/api/sgr';
import type { InvoicingReponse, MissingInfo } from 'lib/api/sgr/types';
import CustomButton from 'lib/designSystem/Button';
import { errorHandler } from 'lib/functions/errors';
import { formatMoney } from 'lib/functions/utils';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';

type Props = { id: number; pruffConnect?: boolean };

const Invoicing: React.FC<Props> = ({ id, pruffConnect = false }) => {
  const dispatch = useDispatch();
  const { mutate, isPending: isEmittingInvoice } =
    emitWarrantyInvoice(dispatch);
  const { data, isPending, error } = getSGRWarrantyRequestInvoicing(
    dispatch,
    id,
  );

  const invoice = (data as InvoicingReponse | undefined | null)?.invoice;

  const missingInfo = useMemo(() => data?.missingInfo, [data]);

  const missingFields = useMemo(() => {
    if (!missingInfo) return [];

    const labels: Record<keyof MissingInfo, string> = {
      activity: 'Actividad económica',
      address: 'Dirección',
      commune: 'Comuna',
      email: 'Correo electrónico',
    };

    return (Object.entries(missingInfo) as [keyof MissingInfo, boolean][])
      .filter(([, value]) => !value)
      .map(([key]) => labels[key]);
  }, [missingInfo]);

  const openInvoiceLink = async () => {
    try {
      window.open(data?.pdf ?? '', '_blank');
    } catch (error) {
      errorHandler(dispatch, error, 'Error al obtener el estado del documento');
    }
  };

  const canInvoice = !missingFields.length;

  const statusLabel = useMemo(() => {
    if (invoice) {
      switch (invoice?.status) {
        case 'PENDING':
          return 'Pendiente';
        case 'REJECTED':
          return 'Rechazada';
        case 'ACCEPTED':
          return 'Aceptada';
        case 'CREDIT_NOTE':
          return 'Nota de crédito';
        default:
          return 'No disponible';
      }
    }
    if (canInvoice) return 'Listo para facturar';
    return 'Información pendiente';
  }, [invoice?.status, canInvoice]);

  const statusChipColor = useMemo(():
    | 'default'
    | 'primary'
    | 'success'
    | 'error'
    | 'warning'
    | 'info' => {
    if (invoice) {
      switch (invoice.status) {
        case 'ACCEPTED':
          return 'success';
        case 'REJECTED':
          return 'error';
        case 'PENDING':
          return 'warning';
        case 'CREDIT_NOTE':
          return 'info';
        default:
          return 'default';
      }
    }
    return canInvoice ? 'success' : 'warning';
  }, [invoice?.status, canInvoice]);

  if (isPending && !error) {
    return (
      <Paper sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={18} />
          <Typography variant="body2">
            Cargando información de facturación…
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <>
      <Paper sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Box
            sx={{
              backgroundColor: 'primary.main',
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <ReceiptIcon sx={{ fontSize: '1rem' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1rem',
                lineHeight: 1.1,
              }}
            >
              Facturación costo de emisión
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Chip
                label={
                  invoice ? `Factura ${invoice.folio}` : 'Sin factura emitida'
                }
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
              <Chip
                label={statusLabel}
                size="small"
                color={statusChipColor}
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
            </Stack>
          </Box>
          {invoice && (
            <Box sx={{ textAlign: 'right' }}>
              <Stack
                direction="row"
                alignItems="start"
                justifyContent="flex-end"
                spacing={0.75}
              >
                <Typography variant="h4" color="primary">
                  {formatMoney(invoice?.amountWithIVA ?? invoice?.amount ?? 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CLP
                </Typography>
              </Stack>
            </Box>
          )}
        </Stack>

        {invoice && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1.5}
            sx={{
              py: 1,
              px: 1.25,
              mb: 1.5,
              borderRadius: 0.5,
              backgroundColor: 'grey.50',
            }}
            flexWrap="wrap"
          >
            <Stack spacing={0.25}>
              <Typography variant="body2" color="text.secondary">
                Tipo documento: {invoice.docType}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Emitida el: {new Date(invoice.issuedDate).toLocaleDateString()}
              </Typography>
            </Stack>
            {data?.pdf && (
              <Tooltip title="Abrir PDF de la factura">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => void openInvoiceLink()}
                  aria-label="Abrir PDF de la factura"
                >
                  <PictureAsPdfIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}

        <Alert
          severity={missingFields.length > 0 ? 'warning' : 'info'}
          icon={<InfoIcon sx={{ fontSize: '0.9rem' }} />}
          sx={{
            borderRadius: 0.5,
            backgroundColor:
              missingFields.length > 0
                ? 'rgba(245, 124, 0, 0.08)'
                : 'rgba(25, 118, 210, 0.08)',
            border:
              missingFields.length > 0
                ? '1px solid rgba(245, 124, 0, 0.2)'
                : '1px solid rgba(25, 118, 210, 0.2)',
            py: 0.75,
            '& .MuiAlert-message': { py: 0.25 },
          }}
        >
          <AlertTitle sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
            Información Importante
          </AlertTitle>
          {missingFields.length > 0 ? (
            <Typography
              variant="body2"
              sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
            >
              Falta completar la siguiente información de la empresa antes de
              poder facturar:
              <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0 }}>
                {missingFields.map((field) => (
                  <li key={field}>{field}</li>
                ))}
              </Box>
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
            >
              Toda la información necesaria para emitir la factura del costo de
              emisión está completa.
            </Typography>
          )}
        </Alert>

        {!invoice && !pruffConnect && (
          <Box sx={{ mt: 2 }}>
            <CustomButton
              size="small"
              onClick={() => mutate({ warrantyRequestIds: [id], manual: true })}
              disabled={isEmittingInvoice || !canInvoice}
              loading={isEmittingInvoice}
            >
              Facturar costo de emisión
            </CustomButton>
          </Box>
        )}
      </Paper>
    </>
  );
};

export default Invoicing;
